import { IntegrationInitialState } from 'integrations/common/initialStateTypes';
import {
  ConnectionFieldDefinition,
  UnifiedIntegrationId,
  UnifiedIntegrationParams,
} from './types';
import { buildInitialTermsState } from 'integrations/common/streams/integrationTermsDictionary/initialState';
import { Paragraph } from '@ardoq/typography';
import { InlineLink } from '@ardoq/link';
import {
  TableMappingType,
  UnifiedAuthMethod,
} from '@ardoq/api-types/integrations';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { Features } from '@ardoq/features';
import { t } from '@ardoq/i18n';
import { integrationNames } from '@ardoq/integrations/integration-sources';
import { i18nLocale$ } from 'streams/i18nLocale$';

const SharePointHelperText = () => {
  // i18nLocale$.state acceptable here: rendered as a static field-definition
  // helperText snapshot at module init; the connection field machinery has no
  // locale-aware rendering hook to thread i18nLocale through.
  const i18nLocale = i18nLocale$.state;
  return (
    <Paragraph variant="caption">
      {t(
        i18nLocale,
        'unifiedIntegration:sharepoint.helperText',
        'You can read more about setting up a SharePoint connection in our'
      )}{' '}
      <InlineLink
        target="_blank"
        opensInNewTabLabel={t(
          i18nLocale,
          'common:labels.opensInNewTab',
          '(new tab)'
        )}
        href={KnowledgeBaseLink.SHAREPOINT_INTEGRATION}
      >
        {t(
          i18nLocale,
          'unifiedIntegration:sharepoint.documentationLink',
          'SharePoint documentation'
        )}
      </InlineLink>
    </Paragraph>
  );
};

// Commonly used fields for integrations that require access key and secret key
export const accessKeySecretKeyFields = (
  integrationName: string,
  accessKeyLabel: string,
  secretKeyLabel: string,
  standardDomain: string,
  urlProtocol: 'http://' | 'https://' = 'https://'
): ConnectionFieldDefinition[] => [
  {
    keyPath: ['baseUrl'],
    label: `${integrationName} base URL`,
    type: 'url',
    urlProtocol,
    standardDomain,
    required: true,
  },
  {
    keyPath: ['accessKey'],
    label: accessKeyLabel,
    type: 'text',
    required: true,
  },
  {
    keyPath: ['secretKey'],
    label: secretKeyLabel,
    type: 'password',
    required: true,
  },
];

export const initialStates: Record<
  UnifiedIntegrationId,
  IntegrationInitialState & UnifiedIntegrationParams
> = {
  'jira-software': {
    activeIntegration: {
      integrationId: 'jira-software',
      integrationMappingParams: {
        mapColumnsBy: 'sourceFieldName',
        mapTablesBy: 'id',
      },
    },
    termsDictionary: buildInitialTermsState({
      name: integrationNames['jira-software'],
      integrationOverviewTitle: 'Jira Software Cloud integration',
      rightSidebarHeader: 'Having trouble with the Jira software integration?',
      rightSideBarFAQ: [
        {
          link: KnowledgeBaseLink.JIRA_SOFTWARE_CLOUD_INTEGRATION,
          text: 'How to import Jira Software Cloud data',
        },
      ],
      connectYourAccountTitle:
        'Connect your Jira account to Ardoq to get started.',
      connectYourAccountBtn: 'Connect to Jira',
    }),
    authMethodFields: {
      basic: {
        label: 'Basic',
        fieldDefinitions: accessKeySecretKeyFields(
          'Jira Software',
          'Jira Software Email',
          'Jira Software API Token',
          '.atlassian.net'
        ),
      },
    },
    defaultAuthMethod: UnifiedAuthMethod.BASIC,
  },
  'entra-id': {
    activeIntegration: {
      integrationId: 'entra-id',
      integrationMappingParams: {
        mapColumnsBy: 'sourceFieldName',
        mapTablesBy: 'id',
      },
    },
    termsDictionary: buildInitialTermsState({
      name: integrationNames['entra-id'],
      integrationOverviewTitle: 'Microsoft Entra ID integration',
      rightSidebarHeader: 'Having trouble with the Microsoft Entra ID?',
      rightSideBarFAQ: [
        {
          link: KnowledgeBaseLink.MICROSOFT_ENTRA_ID_INTEGRATION_UNIFIED,
          text: 'How to import Microsoft Entra ID data',
        },
      ],
      connectYourAccountTitle:
        'Connect your Microsoft Entra ID account to Ardoq to get started.',
      connectYourAccountBtn: 'Connect to Microsoft Entra ID',
    }),
    authMethodFields: {
      basic: {
        label: 'Basic',
        fieldDefinitions: [
          {
            keyPath: ['providerParams', 'tenantId'],
            label: 'Tenant ID',
            type: 'text',
            required: true,
          },
          {
            keyPath: ['accessKey'],
            label: 'Client ID',
            type: 'text',
            required: true,
          },
          {
            keyPath: ['secretKey'],
            label: 'Client Secret',
            type: 'password',
            required: true,
          },
        ],
      },
    },
    defaultAuthMethod: UnifiedAuthMethod.BASIC,
    tabularMappingsDefault: {
      user: {
        defaultRowRepresentation: TableMappingType.COMPONENTS,
        defaultColumnMappings: {
          displayName: {
            columnType: 'component',
            componentType: 'Person',
            hierarchyLevel: 0,
            index: -1,
            sourceFieldName: 'displayName',
          },
          mail: {
            columnType: 'field',
            fieldType: 'Email',
            fieldName: 'contact_email',
            fieldLabel: 'Contact Email',
            index: -1,
            sourceFieldName: 'mail',
          },
        },
      },
    },
  },
  signavio: {
    activeIntegration: {
      integrationId: 'signavio',
      integrationMappingParams: {
        mapColumnsBy: 'sourceFieldName',
        mapTablesBy: 'id',
      },
    },
    termsDictionary: buildInitialTermsState({
      name: integrationNames.signavio,
      integrationOverviewTitle: 'SAP Signavio integration',
      rightSidebarHeader: 'Having trouble with the SAP Signavio integration?',
      rightSideBarFAQ: [
        {
          link: KnowledgeBaseLink.SAP_SIGNAVIO_INTEGRATION,
          text: 'How to import SAP Signavio data',
        },
      ],
      connectYourAccountTitle:
        'Connect your SAP Signavio account to Ardoq to get started.',
      connectYourAccountBtn: 'Connect to SAP Signavio',
    }),
    authMethodFields: {
      basic: {
        label: 'Basic',
        fieldDefinitions: [
          ...accessKeySecretKeyFields(
            'SAP Signavio',
            'SAP Signavio username',
            'SAP Signavio password',
            '.signavio.com'
          ),
          {
            keyPath: ['providerParams', 'tenant-id'],
            label: 'Workspace ID (or Tenant ID)',
            type: 'text',
            required: true,
          },
        ],
      },
    },
    defaultAuthMethod: UnifiedAuthMethod.BASIC,
  },
  ardoq: {
    activeIntegration: {
      integrationId: 'ardoq',
      integrationMappingParams: {
        mapColumnsBy: 'sourceFieldName',
        mapTablesBy: 'id',
      },
    },
    termsDictionary: buildInitialTermsState({
      name: integrationNames.ardoq,
      integrationOverviewTitle: 'Ardoq integration',
      rightSidebarHeader: 'Having trouble with the Ardoq integration?',
      rightSideBarFAQ: [
        {
          link: KnowledgeBaseLink.INTEGRATIONS_COLLECTION,
          text: 'How to import Ardoq data',
        },
      ],
      connectYourAccountTitle:
        'Connect your Ardoq account to Ardoq to get started.',
      connectYourAccountBtn: 'Connect to Ardoq',
    }),
    authMethodFields: {
      basic: {
        label: 'Basic',
        fieldDefinitions: [
          ...accessKeySecretKeyFields(
            'Ardoq',
            'Ardoq username',
            'Arodq password',
            '.ardoq.com'
          ),
        ],
      },
    },
    defaultAuthMethod: UnifiedAuthMethod.BASIC,
  },
  'ardoq-reference-data': {
    activeIntegration: {
      integrationId: 'ardoq-reference-data',
      integrationMappingParams: {
        mapColumnsBy: 'sourceFieldName',
        mapTablesBy: 'name',
      },
    },
    termsDictionary: buildInitialTermsState({
      name: integrationNames['ardoq-reference-data'],
      integrationOverviewTitle: 'Ardoq reference data',
      rightSidebarHeader: 'Having trouble with the Ardoq reference data?',
      rightSideBarFAQ: [
        {
          link: KnowledgeBaseLink.INTEGRATIONS_COLLECTION,
          text: 'How to import Ardoq reference data',
        },
      ],
      connectYourAccountTitle:
        'Connect your Ardoq account to Ardoq to get started.',
      connectYourAccountBtn: 'Connect to Ardoq',
    }),
    authMethodFields: {
      basic: {
        label: 'Basic',
        fieldDefinitions: [
          ...accessKeySecretKeyFields(
            'Ardoq',
            'Ardoq username',
            'Arodq password',
            '.ardoq.com'
          ),
        ],
      },
    },
    defaultAuthMethod: UnifiedAuthMethod.BASIC,
    areConfigurationActionsDisabled: false,
  },
  'ardoq-sample-data': {
    activeIntegration: {
      integrationId: 'ardoq-sample-data',
      integrationMappingParams: {
        mapColumnsBy: 'sourceFieldName',
        mapTablesBy: 'name',
      },
    },
    termsDictionary: buildInitialTermsState({
      name: integrationNames['ardoq-sample-data'],
      integrationOverviewTitle: 'Ardoq sample data',
      rightSidebarHeader: 'Having trouble with the Ardoq sample data?',
      rightSideBarFAQ: [
        {
          link: KnowledgeBaseLink.INTEGRATIONS_COLLECTION,
          text: 'How to import Ardoq sample data',
        },
      ],
      connectYourAccountTitle:
        'Connect your Ardoq account to Ardoq to get started.',
      connectYourAccountBtn: 'Connect to Ardoq',
    }),
    authMethodFields: {
      basic: {
        label: 'Basic',
        fieldDefinitions: [
          ...accessKeySecretKeyFields(
            'Ardoq',
            'Ardoq username',
            'Arodq password',
            '.ardoq.com'
          ),
        ],
      },
    },
    defaultAuthMethod: UnifiedAuthMethod.BASIC,
    areConfigurationActionsDisabled: false,
  },
  'sharepoint-files': {
    activeIntegration: {
      integrationId: 'sharepoint-files',
      integrationMappingParams: {
        mapColumnsBy: 'sourceFieldName',
        mapTablesBy: 'id',
      },
    },
    termsDictionary: buildInitialTermsState({
      name: integrationNames['sharepoint-files'],
      integrationOverviewTitle: 'SharePoint integration',
      rightSidebarHeader: 'Having trouble with the SharePoint importer?',
      rightSideBarFAQ: [
        {
          link: KnowledgeBaseLink.SHAREPOINT_INTEGRATION,
          text: 'How to import SharePoint files',
        },
      ],
      connectYourAccountTitle:
        'Connect your SharePoint account to Ardoq to get started.',
      connectYourAccountBtn: 'Connect to SharePoint',
    }),
    authMethodFields: {
      basic: {
        label: 'Basic',
        fieldDefinitions: [
          {
            keyPath: ['cloud'],
            label: 'Azure cloud.',
            type: 'hidden',
            value: 'azure-cloud',
            required: true,
          },
          {
            keyPath: ['providerParams', 'tenantId'],
            label: 'Tenant ID',
            type: 'text',
            required: true,
            helperText: <SharePointHelperText />,
          },
          ...accessKeySecretKeyFields(
            'SharePoint',
            'Application ID',
            'Client secret',
            ''
          ),
        ],
      },
    },
    defaultAuthMethod: UnifiedAuthMethod.BASIC,
  },
  celonis: {
    activeIntegration: {
      integrationId: 'celonis',
      integrationMappingParams: {
        mapColumnsBy: 'sourceFieldName',
        mapTablesBy: 'id',
      },
    },
    termsDictionary: buildInitialTermsState({
      name: integrationNames.celonis,
      integrationOverviewTitle: 'Celonis Process Intelligence integration',
      rightSidebarHeader: 'Having trouble with the Celonis integration?',
      rightSideBarFAQ: [
        {
          link: KnowledgeBaseLink.CELONIS_PROCESS_INTELLIGENCE_INTEGRATION,
          text: 'How to import Celonis Process Intelligence data',
        },
      ],
      connectYourAccountTitle:
        'Connect your Celonis Process Intelligence account to Ardoq to get started.',
      connectYourAccountBtn: 'Connect to Celonis Process Intelligence',
    }),
    authMethodFields: {
      token: {
        label: 'Token',
        fieldDefinitions: [
          {
            keyPath: ['baseUrl'],
            label: 'Celonis base URL',
            type: 'url',
            urlProtocol: 'https://',
            standardDomain: '.celonis.cloud',
            required: true,
          },
          {
            keyPath: ['token'],
            label: 'Celonis AppKey',
            type: 'text',
            required: true,
          },
          {
            keyPath: ['providerParams', 'kmIds'],
            label: 'Knowledge Models',
            type: 'text',
            required: true,
            helperText: 'Comma separated list of Celonis Knowledge Model IDs',
          },
        ],
      },
      'oauth-client-credentials': {
        label: 'OAuth 2.0',
        fieldDefinitions: [
          {
            keyPath: ['baseUrl'],
            label: 'Celonis base URL',
            type: 'url',
            urlProtocol: 'https://',
            standardDomain: '.celonis.cloud',
            required: true,
          },
          {
            keyPath: ['provider'],
            type: 'hidden',
            label: 'Provider',
            value: 'celonis',
            required: true,
          },
          {
            keyPath: ['providerParams', 'clientId'],
            label: 'ID',
            type: 'text',
            required: true,
          },
          {
            keyPath: ['providerParams', 'clientSecret'],
            label: 'Secret',
            type: 'password',
            required: true,
          },
          {
            keyPath: ['providerParams', 'tokenType'],
            label: 'Token type',
            type: 'hidden',
            required: true,
            value: 'access-token',
          },
          {
            keyPath: ['providerParams', 'scope'],
            label: 'Scope',
            type: 'hidden',
            required: true,
            value: ['studio'],
          },
          {
            keyPath: ['providerParams', 'clientAuthType'],
            label: 'Client authentication type',
            type: 'hidden',
            required: true,
            value: 'basic',
          },
        ],
      },
    },
    defaultAuthMethod: UnifiedAuthMethod.TOKEN,
  },
  'import-builder': {
    activeIntegration: {
      integrationId: 'import-builder',
      integrationMappingParams: {
        mapColumnsBy: 'sourceFieldName',
        mapTablesBy: 'id',
      },
    },
    termsDictionary: buildInitialTermsState({
      name: integrationNames['import-builder'],
      integrationOverviewTitle: 'Import builder integration',
      rightSidebarHeader: 'Having trouble with the Import builder?',
      rightSideBarFAQ: [
        {
          link: KnowledgeBaseLink.IMPORT_BUILDER,
          text: 'How to build custom integrations',
        },
      ],
      connectYourAccountTitle:
        'Create a new connection to get started with your custom integration.',
      connectYourAccountBtn: 'Create connection',
      overviewPrimaryButton: 'Create new import',
    }),
  },
  symbio: {
    activeIntegration: {
      integrationId: 'symbio',
      integrationMappingParams: {
        mapColumnsBy: 'sourceFieldName',
        mapTablesBy: 'id',
      },
    },
    termsDictionary: buildInitialTermsState({
      name: integrationNames.symbio,
      integrationOverviewTitle: 'Celonis Process Management integration',
      rightSidebarHeader:
        'Having trouble with the Celonis Process Management integration?',
      rightSideBarFAQ: [
        {
          link: KnowledgeBaseLink.CELONIS_PROCESS_MANAGEMENT_IMPORTER,
          text: 'How to import Celonis Process Management data',
        },
      ],
      connectYourAccountTitle:
        'Connect your Celonis Process Management account to Ardoq to get started.',
      connectYourAccountBtn: 'Connect to Celonis Process Management',
    }),
    authMethodFields: {
      token: {
        label: 'Token',
        fieldDefinitions: [
          {
            keyPath: ['baseUrl'],
            label: 'Symbio base URL',
            type: 'url',
            urlProtocol: 'https://',
            standardDomain: '.symbioweb.com',
            required: true,
          },
          {
            keyPath: ['token'],
            label: 'Symbio API token',
            type: 'text',
            required: true,
          },
          {
            keyPath: ['providerParams', 'collection'],
            label: 'Collection',
            type: 'text',
            required: true,
          },
          {
            keyPath: ['providerParams', 'storage'],
            label: 'Storage',
            type: 'text',
            required: true,
          },
          {
            keyPath: ['providerParams', 'preferredDataCulture'],
            label: 'Preferred language',
            type: 'text',
            required: true,
          },
        ],
      },
    },
    defaultAuthMethod: UnifiedAuthMethod.TOKEN,
  },
  'azure-resources': {
    activeIntegration: {
      integrationId: 'azure-resources',
      integrationMappingParams: {
        mapColumnsBy: 'sourceFieldName',
        mapTablesBy: 'id',
      },
    },
    termsDictionary: buildInitialTermsState({
      name: integrationNames['azure-resources'],
      integrationOverviewTitle: 'Azure Resources integration',
      rightSidebarHeader:
        'Having trouble with the Azure Resources integration?',
      rightSideBarFAQ: [
        {
          link: KnowledgeBaseLink.AZURE_RESOURCES_INTEGRATION,
          text: 'How to import Azure Resources data',
        },
      ],
      connectYourAccountTitle:
        'Connect your Azure account to Ardoq to get started.',
      connectYourAccountBtn: 'Connect to Azure',
      overviewWarning: {
        i18nKey: 'unifiedIntegration:azureResources.overview.migrationWarning',
        defaultValue:
          'Azure Resources integration uses lowercased Resource IDs; the legacy Azure integration does not. Running both simultaneously can create duplicate components. Use only Azure Resources integration to avoid this.',
        requiredFeature: Features.AZURE_ENABLE,
      },
    }),
    authMethodFields: {
      basic: {
        label: 'Basic',
        fieldDefinitions: [
          {
            keyPath: ['providerParams', 'tenantId'],
            label: 'Tenant ID',
            type: 'text',
            required: true,
          },
          {
            keyPath: ['accessKey'],
            label: 'Client ID',
            type: 'text',
            required: true,
          },
          {
            keyPath: ['secretKey'],
            label: 'Client Secret',
            type: 'password',
            required: true,
          },
        ],
      },
    },
    defaultAuthMethod: UnifiedAuthMethod.BASIC,
  },
};
